import React from "react";

export default function TrainingRunCurvesChart(): JSX.Element {
  // Set dimensions and margins
  const width = 1000;
  const height = 700;
  const margin = { top: 60, right: 40, bottom: 60, left: 120 };
  const innerWidth = width - margin.left - margin.right;
  const innerHeight = height - margin.top - margin.bottom;

  // Month labels for x-axis spanning from Oct 2024 to Nov 2027
  const months: string[] = [
    "2024-11", "2024-12", "2025-01", "2025-02", "2025-03", "2025-04", "2025-05",
    "2025-06", "2025-07", "2025-08", "2025-09", "2025-10", "2025-11", "2025-12", "2026-01",
    "2026-02", "2026-03", "2026-04", "2026-05", "2026-06", "2026-07", "2026-08", "2026-09",
    "2026-10", "2026-11", "2026-12", "2027-01", "2027-02", "2027-03", "2027-04", "2027-05",
    "2027-06", "2027-07", "2027-08", "2027-09", "2027-10", "2027-11", "2027-12"
  ];
  
  // Formatted month labels for display (e.g., "Jun 2027" instead of "2027-06")
  const displayMonths: string[] = months.map(m => {
    const [year, month] = m.split("-");
    const date = new Date(parseInt(year), parseInt(month) - 1, 1);
    return new Intl.DateTimeFormat('en-US', { month: 'short', year: 'numeric' }).format(date);
  });

  // Create arrays with zeros for each agent, then fill in the data at the correct positions
  const createFullDataArray = (startIndex: number, values: number[]): number[] => {
    const result: number[] = Array(months.length).fill(0);
    for (let i = 0; i < values.length; i++) {
      if (startIndex + i < result.length) {
        result[startIndex + i] = values[i];
      }
    }
    return result;
  };

  // Agent-1 data (Oct 2024 to May 2025)
  const agent0Values: number[] = [1.5E+26, 2.9E+26, 4.4E+26, 5.8E+26, 6.5E+26, 7.3E+26, 8.1E+26, 8.9E+26];
  const agent0Data: number[] = createFullDataArray(0, agent0Values);

  // Agent-1.5 data (Jul 2025 to Feb 2026)
  const agent1Values: number[] = [5.18E+26, 1.04E+27, 1.56E+27, 2.07E+27, 2.33E+27, 2.59E+27, 2.85E+27, 3.11E+27];
  const agent1Data: number[] = createFullDataArray(9, agent1Values);

  // Agent-2 data (Apr 2026 to Mar 2027)
  const agent2Values: number[] = [
    1.87E+27, 3.73E+27, 5.60E+27, 7.46E+27, 9.33E+27,
    1.12E+28, 1.31E+28, 1.49E+28, 1.68E+28, 1.87E+28,
    2.05E+28, 2.24E+28
  ];
  const agent2Data: number[] = createFullDataArray(18, agent2Values);

  // Agent-3 data (Mar 2027 to Aug 2027)
  const agent3Values: number[] = [2.24E+28, 2.33E+28, 2.43E+28, 2.52E+28, 2.61E+28, 2.71E+28];
  const agent3Data: number[] = createFullDataArray(29, agent3Values);

  // Agent-4 data (Aug 2027 to Nov 2027)
  const agent4Values: number[] = [2.71E+28, 2.87E+28, 3.02E+28, 3.18E+28];
  const agent4Data: number[] = createFullDataArray(34, agent4Values);

  // Reference lines for GPT-4 and 1000x GPT-4
  const gpt4Line: number = 2.0e25; // GPT-4 reference line
  const gpt45Line: number = 2.0e26; // GPT-4 reference line
  const gpt41000xLine: number = 2.0e28; // 1000x GPT-4 reference line

  // Maximum value for scaling
  const minValue: number = 1.0e25; // Minimum y-value (log scale)
  const maxValue: number = 4.0e28; // Maximum y-value for plotting

  // Scales: x based on index, y based on logarithmic scale
  const xScale = (i: number): number => margin.left + (i / (months.length - 1)) * innerWidth;
  
  // Logarithmic y-scale
  const yScale = (value: number): number => {
    if (value <= 0) return margin.top + innerHeight; // Handle zero or negative values
    const logMin = Math.log10(minValue)-.1;
    const logMax = Math.log10(maxValue)+.01;
    const logValue = Math.log10(value);
    return margin.top + innerHeight - ((logValue - logMin) / (logMax - logMin)) * innerHeight;
  };

  // Generate x-values for all data points
  const xValues: number[] = months.map((_, i) => xScale(i));

  // Generate y-axis ticks (log scale)
  const yTicks: number[] = [1e25, 1e26, 1e27, 1e28];
  
  // Choose select x-axis tick indices for clarity
  const xTickIndices: number[] = [1, 7, 13, 19, 25, 31, 37];
  
  // Line generation helper
  const generateLinePath = (data: number[]): string => {
    const points: {x: number, y: number, value: number}[] = data
      .map((d, i) => ({ x: xScale(i), y: yScale(d), value: d }))
      .filter(p => p.value > 0); // Filter out zero values
    
    if (points.length === 0) return "";
    
    return points
      .map((p, i) => `${i === 0 ? "M" : "L"} ${p.x} ${p.y}`)
      .join(" ");
  };

  // Create paths for each agent's data
  const agent1Path: string = generateLinePath(agent0Data);
  const agent15Path: string = generateLinePath(agent1Data);
  const agent2Path: string = generateLinePath(agent2Data);
  const agent3Path: string = generateLinePath(agent3Data);
  const agent4Path: string = generateLinePath(agent4Data);

  // Define colors for each agent with more distinct shades
  const colors = {
    agent0: "rgba(45, 45, 45, 1)",        // Darker grey for agent 1
    agent1: "rgba(120, 120, 120, 1)",       // Slightly different shade for agent 1.5
    agent2: "rgba(0, 140, 0, 0.5)",         // Brighter green for Agent-2
    agent3: "rgba(0, 90, 0, 0.7)",          // Medium green for Agent-3
    agent4: "rgba(0, 50, 0, .9)",          // Darkest green for Agent-4
    agent2t: "rgba(0, 140, 0, 1)",         // Brighter green for Agent-2
    agent3t: "rgba(0, 90, 0, 1)",          // Medium green for Agent-3
    agent4t: "rgba(0, 50, 0, 1)",          // Darkest green for Agent-4
    grid: "#cccccc",                        // Light gray for grid
    text: "#333333"                         // Dark gray for text
  };

  // Helper to find last valid index for each dataset (for labeling)
  const getLastValidIndex = (data: number[]): number => {
    for (let i = data.length - 1; i >= 0; i--) {
      if (data[i] > 0) return i;
    }
    return -1;
  };

  // Get indices for labeling each line
  const agent1LabelIndex: number = getLastValidIndex(agent0Data);
  const agent15LabelIndex: number = getLastValidIndex(agent1Data);
  const agent2LabelIndex: number = getLastValidIndex(agent2Data);
  const agent3LabelIndex: number = getLastValidIndex(agent3Data);
  const agent4LabelIndex: number = getLastValidIndex(agent4Data);

  return (
    <div className="w-full relative overflow-hidden mt-8 mb-8">
      <svg
        width="100%"
        preserveAspectRatio="xMidYMid meet"
        viewBox={`0 0 ${width} ${height}`}
      >
        {/* Chart Title */}
        <text
          x={margin.left}
          y={margin.top-5}
          fontSize="32"
          fontFamily="monospace"
          textAnchor="left"
          className="text-[32px] font-mono md:text-[32px] sm:text-[32px]"
          fill={colors.text}
        >
          Training Runs
        </text>

        {/* Y-axis label - moved more to the left */}
        <text
          transform={`rotate(-90, ${margin.left - 60}, ${margin.top + innerHeight/2})`}
          x={margin.left - 60}
          y={margin.top + innerHeight/2.3}
          textAnchor="middle"
          className="text-[20px] font-mono"
          fill={colors.text}
        >
          Training Compute in Fp16 FLOP (log scale)
        </text>

        {/* X-axis label */}
        {/* <text
          x={margin.left + innerWidth/2}
          y={height - 5}
          textAnchor="middle"
          className="text-[18px] font-mono"
          fill={colors.text}
        >
          Date
        </text> */}

        {/* Grid lines - vertical */}
        {xValues.map((x, i) => (
          <line
            key={`vgrid-${i}`}
            x1={x}
            y1={margin.top}
            x2={x}
            y2={margin.top + innerHeight}
            stroke={colors.grid}
            strokeWidth="1"
            opacity="0.3"
          />
        ))}

        {/* Grid lines - horizontal (log scale) */}
        {yTicks.map((tick, i) => (
          <line
            key={`hgrid-${i}`}
            x1={margin.left}
            y1={yScale(tick)}
            x2={margin.left + innerWidth}
            y2={yScale(tick)}
            stroke={colors.grid}
            strokeWidth="1"
            opacity="0.3"
          />
        ))}

        {/* Reference line for GPT-4 */}
        <line
          x1={margin.left}
          y1={yScale(gpt4Line)}
          x2={margin.left + innerWidth}
          y2={yScale(gpt4Line)}
          stroke="#888888"
          strokeWidth="1.5"
          strokeDasharray="5,5"
          opacity="0.7"
        />
        <text
          x={margin.left+5}
          y={yScale(gpt4Line) +20}
          textAnchor="start"
          className="text-[16px] font-mono"
          fill="#888888"
        >
          GPT-4 (2e25)
        </text>

        <line
          x1={margin.left}
          y1={yScale(gpt45Line)}
          x2={margin.left + innerWidth}
          y2={yScale(gpt45Line)}
          stroke="#888888"
          strokeWidth="1.5"
          strokeDasharray="5,5"
          opacity="0.7"
        />
        <text
          x={margin.left+700}
          y={yScale(gpt45Line) +20}
          textAnchor="start"
          className="text-[16px] font-mono"
          fill="#888888"
        >
          GPT-4.5 (2e26)
        </text>

        {/* Reference line for 1000x GPT-4 */}
        <line
          x1={margin.left}
          y1={yScale(gpt41000xLine)}
          x2={margin.left + innerWidth}
          y2={yScale(gpt41000xLine)}
          stroke="#888888"
          strokeWidth="1.5"
          strokeDasharray="5,5"
          opacity="0.7"
        />
        <text
          x={margin.left+5}
          y={yScale(gpt41000xLine) +20}
          textAnchor="start"
          className="text-[16px] font-mono"
          fill="#888888"
        >
          1000xGPT-4 (2e28)
        </text>

        {/* X-axis ticks and labels - using formatted month display */}
        {xTickIndices.map((index) => (
          <g key={`xtick-${index}`}>
            <line
              x1={xValues[index]}
              y1={margin.top + innerHeight}
              x2={xValues[index]}
              y2={margin.top + innerHeight + 5}
              stroke={colors.text}
              strokeWidth="1.5"
            />
            <text
              x={xValues[index]}
              y={margin.top + innerHeight + 25}
              textAnchor="middle"
              className="text-[16px] font-mono"
              fill={colors.text}
            >
              {displayMonths[index]}
            </text>
          </g>
        ))}

        {/* Y-axis ticks and labels */}
        {yTicks.map((tick) => (
          <g key={`ytick-${tick}`}>
            <line
              x1={margin.left - 5}
              y1={yScale(tick)}
              x2={margin.left}
              y2={yScale(tick)}
              stroke={colors.text}
              strokeWidth="1.5"
            />
            <text
              x={margin.left - 10}
              y={yScale(tick) + 5}
              textAnchor="end"
              className="text-[16px] font-mono"
              fill={colors.text}
            >
              {`10^${Math.log10(tick).toFixed(0)}`}
            </text>
          </g>
        ))}

        {/* Agent-0 line and data points - with thicker line */}
        <path
          d={agent1Path}
          fill="none"
          stroke={colors.agent0}
          strokeWidth="4"
        />
        {agent0Data.map((d, i) => d > 0 && (
          <circle
            key={`agent1-${i}`}
            cx={xScale(i)}
            cy={yScale(d)}
            r={4.5}
            fill={colors.agent0}
            stroke="white"
            strokeWidth="1"
          />
        ))}

        {/* Agent-1 line and data points - with thicker line */}
        <path
          d={agent15Path}
          fill="none"
          stroke={colors.agent1}
          strokeWidth="4"
        />
        {agent1Data.map((d, i) => d > 0 && (
          <circle
            key={`agent15-${i}`}
            cx={xScale(i)}
            cy={yScale(d)}
            r={4.5}
            fill={colors.agent1}
            stroke="white"
            strokeWidth="1"
          />
        ))}

        {/* Agent-2 line and data points - with thicker line */}
        <path
          d={agent2Path}
          fill="none"
          stroke={colors.agent2}
          strokeWidth="4"
        />
        {agent2Data.map((d, i) => d > 0 && (
          <circle
            key={`agent2-${i}`}
            cx={xScale(i)}
            cy={yScale(d)}
            r={4.5}
            fill={colors.agent2}
            stroke="white"
            strokeWidth="1"
          />
        ))}

        {/* Agent-3 line and data points - with thicker line */}
        <path
          d={agent3Path}
          fill="none"
          stroke={colors.agent3}
          strokeWidth="4"
        />
        {agent3Data.map((d, i) => d > 0 && (
          <circle
            key={`agent3-${i}`}
            cx={xScale(i)}
            cy={yScale(d)}
            r={4.5}
            fill={colors.agent3}
            stroke="white"
            strokeWidth="1"
          />
        ))}

        {/* Agent-4 line and data points - with thicker line */}
        <path
          d={agent4Path}
          fill="none"
          stroke={colors.agent4}
          strokeWidth="4"
        />
        {agent4Data.map((d, i) => d > 0 && (
          <circle
            key={`agent4-${i}`}
            cx={xScale(i)}
            cy={yScale(d)}
            r={4.5}
            fill={colors.agent4}
            stroke="white"
            strokeWidth="1"
          />
        ))}

        {/* Legend labels repositioned to match the reference image */}
        <text
          x={margin.left+212}
          y={margin.bottom+315}
          textAnchor="start"
          className="text-[18px] font-mono"
          fill={colors.agent1}
        >
          Agent-1
        </text>

        <text
          x={margin.left+9}
          y={margin.bottom+400}
          textAnchor="start"
          className="text-[18px] font-mono"
          fill={colors.agent0}
        >
          Agent-0
        </text>

        <text
          x={margin.left+417}
          y={margin.bottom+225}
          textAnchor="start"
          className="text-[18px] font-mono"
          fill={colors.agent2t}
        >
          Agent-2
        </text>

        <text
          x={margin.left+660}
          y={margin.bottom+65}
          textAnchor="start"
          className="text-[18px] font-mono"
          fill={colors.agent3t}
        >
          Agent-3
        </text>

        <text
          x={margin.left+765}
          y={margin.bottom+5}
          textAnchor="start"
          className="text-[18px] font-mono"
          fill={colors.agent4t}
        >
          Agent-4
        </text>
      </svg>
    </div>
  );
}